import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, lazy, Suspense } from 'react';
import { AlertCircle, Plus } from 'lucide-react';
import { toast } from 'sonner';

import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTableSkeleton } from '@/components/ui/data-table-skeleton';
import { EmployeeTable, EmployeeFilterBar } from '@/components/employees';
import {
  useEmployees,
  useCreateEmployee,
  useUpdateEmployee,
  useDeactivateEmployee,
} from '@/lib/hooks/useEmployees';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import type { EmployeeFilters as UIEmployeeFilters, Employee } from '@repo/types';
import type { EmployeeFilters as APIEmployeeFilters } from '@/lib/api/employees';
import type { EmployeeFormData } from '@/components/employees/AddEmployeeDialog';
import type { EmployeeUpdateData } from '@/components/employees/EditEmployeeDialog';

const AddEmployeeDialog = lazy(() =>
  import('@/components/employees/AddEmployeeDialog').then((m) => ({ default: m.AddEmployeeDialog }))
);
const EditEmployeeDialog = lazy(() =>
  import('@/components/employees/EditEmployeeDialog').then((m) => ({ default: m.EditEmployeeDialog }))
);
const DeactivateEmployeeDialog = lazy(() =>
  import('@/components/employees/DeactivateEmployeeDialog').then((m) => ({
    default: m.DeactivateEmployeeDialog,
  }))
);

export const Route = createFileRoute('/admin/employees/')({
  component: EmployeesPage,
});

function EmployeesPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<UIEmployeeFilters>({
    role: 'all',
    status: 'all',
    team: 'all',
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);

  const debouncedSearch = useDebouncedValue(searchQuery, 300);

  const apiFilters: APIEmployeeFilters = {
    role: filters.role !== 'all' ? filters.role : undefined,
    status: filters.status !== 'all' ? filters.status : undefined,
    teamId: filters.team !== 'all' ? filters.team : undefined,
    search: debouncedSearch || undefined,
  };

  const { data: employeesData, isLoading, error } = useEmployees(apiFilters, page, pageSize);

  const createEmployeeMutation = useCreateEmployee();
  const updateEmployeeMutation = useUpdateEmployee();
  const deactivateEmployeeMutation = useDeactivateEmployee();

  const handleFiltersChange = (next: UIEmployeeFilters) => {
    setFilters(next);
    setPage(1);
  };
  const handleSearchChange = (q: string) => {
    setSearchQuery(q);
    setPage(1);
  };
  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value));
    setPage(1);
  };

  const handleRowClick = (employee: Employee) =>
    navigate({ to: '/admin/employees/$id', params: { id: employee.id } });

  const handleAddEmployee = async (data: EmployeeFormData) => {
    try {
      await createEmployeeMutation.mutateAsync(data);
      toast.success('Employee created successfully');
      setAddDialogOpen(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create employee');
    }
  };

  const handleEditEmployee = async (data: EmployeeUpdateData) => {
    if (!selectedEmployee) return;
    try {
      await updateEmployeeMutation.mutateAsync({ id: selectedEmployee.id, data });
      toast.success('Employee updated successfully');
      setEditDialogOpen(false);
      setSelectedEmployee(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update employee');
    }
  };

  const handleEditClick = (employee: Employee) => {
    setSelectedEmployee(employee);
    setEditDialogOpen(true);
  };

  const handleDeactivateClick = (employee: Employee) => {
    setSelectedEmployee(employee);
    setDeactivateDialogOpen(true);
  };

  const handleDeactivateConfirm = async () => {
    if (!selectedEmployee) return;
    try {
      await deactivateEmployeeMutation.mutateAsync(selectedEmployee.id);
      toast.success('Employee deactivated successfully');
      setDeactivateDialogOpen(false);
      setSelectedEmployee(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to deactivate employee');
    }
  };

  const total = employeesData?.total ?? 0;
  const totalPages = employeesData?.totalPages ?? 1;
  const rangeStart = total ? (page - 1) * pageSize + 1 : 0;
  const rangeEnd = Math.min(page * pageSize, total);

  return (
    <div className="space-y-6 p-6 duration-300 animate-in fade-in">
      <PageHeader
        title="Employees"
        description="Manage employee accounts, roles and team assignments."
        actions={
          <Button size="sm" onClick={() => setAddDialogOpen(true)}>
            <Plus className="size-4" />
            Add Employee
          </Button>
        }
      />

      <EmployeeFilterBar
        filters={filters}
        searchQuery={searchQuery}
        onFiltersChange={handleFiltersChange}
        onSearchChange={handleSearchChange}
      />

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertDescription>Failed to load employees. Please try again.</AlertDescription>
        </Alert>
      )}

      {isLoading ? (
        <DataTableSkeleton columns={4} />
      ) : (
        employeesData && (
          <>
            <EmployeeTable
              data={employeesData.data}
              onRowClick={handleRowClick}
              onEditClick={handleEditClick}
              onDeactivateClick={handleDeactivateClick}
            />

            {total > 0 && (
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-muted-foreground">
                  Showing <span className="font-medium text-foreground">{rangeStart}</span>–
                  <span className="font-medium text-foreground">{rangeEnd}</span> of{' '}
                  <span className="font-medium text-foreground">{total}</span> employees
                </p>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Rows</span>
                    <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                      <SelectTrigger className="h-8 w-[72px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[10, 20, 50, 100].map((n) => (
                          <SelectItem key={n} value={String(n)}>
                            {n}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <span className="text-sm text-muted-foreground">
                    Page {page} of {totalPages}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </>
        )
      )}

      {/* Dialogs */}
      <Suspense fallback={null}>
        {addDialogOpen && (
          <AddEmployeeDialog
            open={addDialogOpen}
            onOpenChange={setAddDialogOpen}
            onSubmit={handleAddEmployee}
          />
        )}
      </Suspense>

      {selectedEmployee && (
        <Suspense fallback={null}>
          {editDialogOpen && (
            <EditEmployeeDialog
              open={editDialogOpen}
              onOpenChange={setEditDialogOpen}
              onSubmit={handleEditEmployee}
              employee={selectedEmployee}
            />
          )}
          {deactivateDialogOpen && (
            <DeactivateEmployeeDialog
              open={deactivateDialogOpen}
              onOpenChange={setDeactivateDialogOpen}
              onConfirm={handleDeactivateConfirm}
              employee={selectedEmployee}
            />
          )}
        </Suspense>
      )}
    </div>
  );
}
